import { FC, useMemo } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { useAtom, useAtomValue } from 'jotai';
import { InlineNotification, Tag } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { FloatingTooltip } from 'components/floating-tooltip';
import { LinkCell } from 'components/link-cell';
import { StatsBar } from 'components/stats-bar';
import { formatUnitAgreementType } from 'components/allocation-manager/utils';
import { format0, formatPercentScale } from 'utils/number';
import { wellStatusLabel } from 'utils/well-status';
import {
  latestUnitWellAssignmentsAtom,
  unitWellAssignmentFiltersAtom,
  unitWellAssignmentsAtom,
} from './atoms';
import { AssignmentsCell } from './assignments-cell';
import { FilterBar } from './filter-bar';
import { useRefreshState } from './hooks';
import { RefreshBar } from './refresh-bar';
import {
  ASSIGNMENT_TYPE_LABELS,
  TIER_LABELS,
  UNMEASURABLE_REASON_LABELS,
  type UnitWellAssignmentRow,
} from './types';
import styles from './page.module.scss';

const TITLE = 'Unit Well Assignments';

const feet = (value: number | null) => format0(value, '—');

// "no segment" only when no tier answered. A row WITH a segment and no percent
// could not be measured against its unit, which is a data problem to look at.
const PercentCell: FC<{ row: UnitWellAssignmentRow }> = ({ row }) => {
  if (row.percentInUnit !== null)
    return <>{formatPercentScale(row.percentInUnit, '—', 2)}</>;
  if (row.tier === 'none')
    return (
      <Tag type="gray" size="sm">
        no segment
      </Tag>
    );
  return (
    <FloatingTooltip
      content={UNMEASURABLE_REASON_LABELS[row.unmeasurableReason ?? 'other']}
    >
      <Tag type="red" size="sm">
        not measurable
      </Tag>
    </FloatingTooltip>
  );
};

const columns: ColumnDef<UnitWellAssignmentRow>[] = [
  {
    id: 'wellUWI',
    accessorKey: 'wellUWI',
    header: 'UWI',
    size: 130,
    cell: (info) => (
      <LinkCell
        value={info.getValue() as string}
        href={info.row.original.wellHref}
      />
    ),
  },
  {
    id: 'wellName',
    accessorKey: 'wellName',
    header: 'Well Name',
    size: 240,
    cell: (info) => (info.getValue() as string | null) ?? '—',
  },
  {
    id: 'wellStatus',
    accessorKey: 'wellStatus',
    header: 'Status',
    size: 130,
    cell: (info) => wellStatusLabel(info.getValue() as string | null) || '—',
  },
  {
    id: 'currentOperator',
    accessorKey: 'currentOperator',
    header: 'Operator',
    size: 240,
    cell: (info) => (info.getValue() as string | null) ?? '—',
  },
  {
    id: 'developmentAreaName',
    accessorKey: 'developmentAreaName',
    header: 'Assigned Unit',
    size: 150,
    cell: (info) => (
      <LinkCell
        value={info.getValue() as string}
        href={info.row.original.developmentAreaHref}
      />
    ),
  },
  {
    id: 'unitAgreementType',
    accessorKey: 'unitAgreementType',
    header: 'Unit Agreement',
    size: 130,
    enableSorting: false,
    cell: (info) => {
      // The page's em-dash for none, not the allocation manager's placeholder.
      const type = info.row.original.unitAgreementType;
      return type ? formatUnitAgreementType(type) : '—';
    },
  },
  {
    id: 'assignmentType',
    accessorKey: 'assignmentType',
    header: 'Assignment Type',
    size: 140,
    cell: (info) => ASSIGNMENT_TYPE_LABELS[info.row.original.assignmentType],
  },
  {
    id: 'wellAssignmentCount',
    accessorKey: 'wellAssignmentCount',
    header: 'Assignments',
    size: 240,
    enableSorting: false,
    cell: (info) => <AssignmentsCell row={info.row.original} />,
  },
  {
    id: 'tier',
    accessorKey: 'tier',
    header: 'Producing Segment Tier',
    size: 210,
    cell: (info) => TIER_LABELS[info.row.original.tier],
  },
  {
    id: 'producingLengthFt',
    accessorKey: 'producingLengthFt',
    header: 'Producing Length (ft)',
    size: 170,
    cell: (info) => feet(info.getValue() as number | null),
  },
  {
    id: 'lengthInUnitFt',
    accessorKey: 'lengthInUnitFt',
    header: 'Length in Unit (ft)',
    size: 160,
    cell: (info) => feet(info.getValue() as number | null),
  },
  {
    id: 'percentInUnit',
    accessorKey: 'percentInUnit',
    header: 'Percent of Producing Segment in Unit',
    size: 260,
    cell: (info) => <PercentCell row={info.row.original} />,
  },
];

// What an empty table says. Nothing when the fetch failed: the notification
// above it already does.
const emptyMessage = (failed: boolean, neverRefreshed: boolean) => {
  if (failed) return '';
  if (neverRefreshed)
    return 'Not measured yet: refresh to measure every assignment';
  return 'No unit well assignments match these filters';
};

export const UnitWellAssignmentsPage: FC = () => {
  const [filters, setFilters] = useAtom(unitWellAssignmentFiltersAtom);
  const loadable = useAtomValue(unitWellAssignmentsAtom);
  const response = useAtomValue(latestUnitWellAssignmentsAtom);
  const refresh = useRefreshState();
  const rows = useMemo(() => response?.data ?? [], [response]);
  const total = response?.stats.total ?? 0;
  const neverRefreshed = response?.refresh.lastRefreshedAt === null;
  const failed = loadable.state === 'hasError';

  // Server-driven: the table reports the sort, the filters atom owns it, and a
  // new sort returns to page 1. Sort removal is off, so there is always a sort.
  const handleSortChange = (sorting: SortingState) => {
    const sort = sorting[0];
    if (!sort) return;
    setFilters((previous) => ({
      ...previous,
      page: 1,
      sortBy: sort.id,
      sortDir: sort.desc ? 'desc' : 'asc',
    }));
  };

  return (
    <>
      <PageHeader breadcrumbs={[]} title={TITLE} actions={null} />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Every current unit well assignment, with the share of the well&apos;s
          producing segment that lies inside its assigned unit. The segment is
          the one pricing measures the producing length on: the survey
          perforated lateral, its stored estimate, an estimate from the survey
          path, the representative pre-drill plan, then the stick. Measured for
          every assignment nightly, or on demand.
        </div>
        <div className={styles.expectedState}>
          For GIS review only. Pricing does not read this percent.
        </div>
      </div>

      <RefreshBar
        lastRefreshedAt={refresh.lastRefreshedAt}
        state={refresh.state}
      />

      {/* A failed fetch has no total to show; "Total 0" would read as no rows. */}
      {!failed && (
        <StatsBar stats={[{ label: 'Total', value: total, tagType: 'blue' }]} />
      )}

      <FilterBar />

      {/* The one place a failed fetch is reported: the table is told no error. */}
      {failed && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Failed to load unit well assignments"
          subtitle="Reload the page to try again."
        />
      )}

      <DataTable
        data={rows}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={loadable.state === 'loading'}
        error={null}
        emptyMessage={emptyMessage(failed, neverRefreshed)}
        persistenceKey="unit-well-assignments"
        controlledState={{
          pagination: { page: filters.page, pageSize: filters.pageSize },
          sorting: [{ id: filters.sortBy, desc: filters.sortDir === 'desc' }],
        }}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: total,
        }}
        onSortChange={(state) => handleSortChange(state.sorting)}
        onPaginationChange={(state) =>
          setFilters((previous) => ({
            ...previous,
            page: state.page,
            pageSize: state.pageSize,
          }))
        }
        config={{
          sorting: true,
          sortingRemoval: false,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          columnVisibility: true,
          globalSearch: false,
          columnFilters: false,
        }}
      />
    </>
  );
};
